<template>
  <div class="pitch-freelancer-card">
    <WdsStack justify-space-between>
      <WdsAvatarLabel
        :path="getAvatarPath(avatarPath, globalId)"
        :initials="initials"
        :text="name"
        :sub-text="email"
        :text-link="profileUrl"
      />
      <i class="close-icon zmdi zmdi-close" @click="removeFreelancer" />
    </WdsStack>
    <div class="rate-title f-500">{{ $t('Availability') }}</div>
    <WdsRadioGroup v-model:checked="selectedAvailability" :name="`${globalId}-availability`">
      <WdsRadio :name="`${globalId}-availability`" value="immediately">
        {{ $t('Immediately') }}
      </WdsRadio>
      <WdsRadio :name="`${globalId}-availability`" value="starting_from">
        {{ $t('Fixed date') }}
      </WdsRadio>
    </WdsRadioGroup>
    <PickerDatetime
      v-if="selectedAvailability === 'starting_from'"
      v-model="availableFrom"
      as-input
      float-label
      format="YYYY-MM-DD"
      :label="$t('Starting from')"
      required
      class="tw-ml-6 tw-pl-0.5 tw-my-2"
      name="date_start"
    />
    <div class="tw-my-2 f-500">{{ $t('Rate') }}</div>
    <div class="tw-flex tw-gap-x-2">
      <div class="rate-input-wrapper">
        <InputCurrency
          v-model="rateObj"
          :currency="currency"
          placeholder="00"
          data-test-prefix="freelancer-rate-"
          @update:model-value="updateRate"
        />
        <div v-if="errors?.rate?.at(0)" class="tw-text-red-500">{{ errors.rate[0] }}</div>
      </div>
      <div class="tw-grow">
        <WdsSelect
          :start-selected="candidate.rateType"
          :placeholder="$t('Select rate type')"
          :list="[
            { value: rateConstants.HOURLY, label: $t('Per hour') },
            { value: rateConstants.DAILY, label: $t('Per day') },
            { value: rateConstants.MONTHLY, label: $t('Per month') },
            { value: rateConstants.FIXED, label: $t('Fixed budget') },
          ]"
          @select-option="handleRateTypeSelected"
        />
        <div v-if="errors?.rateType?.at(0)" class="tw-text-red-500">{{ errors.rateType[0] }}</div>
      </div>
    </div>
    <div class="f-500 tw-mb-2">{{ $t('Attachments') }}</div>
    <div class="tw-ml-1.5 tw-mb-2">
      <div
        v-for="file in files"
        :key="file.globalId"
        class="tw-flex tw-items-center tw-gap-x-2 tw-mb-2"
      >
        <WdsIcon name="file" :size="16" />
        <a
          :href="laravelNamedRoute(namedRoutes.files.download, { fileId: file.localId })"
          class="tw-font-medium tw-grow"
          target="_blank"
        >
          {{ file.name }}
        </a>
        <WdsIcon
          name="close"
          class="tw-cursor-pointer tw-justify-self-end tw-text-gray-600"
          @click="removeFile(file.globalId)"
        />
      </div>
    </div>
    <PickerFileFilebank
      v-slot="{ openFilePicker }"
      :exclude-ids="files.map(({ localId }) => localId)"
      @files-selected="uploadFiles"
    >
      <WdsButtonGhost primary class="tw-flex tw-items-center tw-gap-x-2" @click="openFilePicker">
        <WdsIcon name="upload" size="18" />
        {{ $t('Upload files') }}
      </WdsButtonGhost>
    </PickerFileFilebank>
    <div class="f-500 tw-mb-2 tw-mt-4">{{ $t('Links') }}</div>
    <div v-for="(link, index) in links" :key="link.key" class="tw-mb-2.5">
      <div class="tw-flex tw-items-center">
        <div class="tw-grow">
          <WdsInput v-model="links[index].url" prefix-icon="link" :name="link.key" />
        </div>
        <WdsIcon
          name="close"
          class="tw-cursor-pointer tw-text-gray-600 tw-ml-1"
          @click="removeLink(index)"
        />
      </div>
      <div v-if="errors?.[`links.${index}`]" class="tw-text-red-500">
        {{ errors[`links.${index}`][0] }}
      </div>
    </div>
    <div class="tw-mb-4">
      <WdsButtonGhost primary class="tw-flex tw-items-center tw-gap-x-2" @click="addLink('')">
        <WdsIcon name="plus" size="18" />
        {{ !links.length ? $t('Add link') : $t('Add another link') }}
      </WdsButtonGhost>
    </div>
    <div v-if="editingComment" class="comment-wrapper">
      <BaseTextareaAutoresize
        :id="`freelancer-comment-${globalId}`"
        ref="freelancerComment"
        v-model="comment"
        :name="`freelancer-comment-${globalId}`"
        additional-classes="freelancer-comment"
        :placeholder="`${$t('Add comment')}...`"
      />
      <WdsStack class="comment-actions" gap-sm>
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <WdsButton @click="cancelComment">{{ $t('Cancel') }}</WdsButton>
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <WdsButton primary size="sm" :disabled="comment === initialComment" @click="saveComment">
          {{ $t('Save') }}
        </WdsButton>
      </WdsStack>
    </div>
    <WdsStack
      v-else-if="comment"
      justify-space-between
      class="existing-comment"
      @click="openTextArea"
    >
      {{ comment }}
      <div v-click-outside="closeMenu" class="comment-menu" @click="openMenu">
        <i class="fa fa-ellipsis-v" />
        <div v-show="isMenuOpen" class="comment-menu-item">
          <WdsStack align-center gap-sm class="comment-menu-item-delete" @click="deleteComment">
            <i class="fa fa-trash-o" />
            <span>{{ $t('Delete comment') }}</span>
          </WdsStack>
        </div>
      </div>
    </WdsStack>
    <WdsStack v-else>
      <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
      <WdsButton
        class="add-comment-button"
        data-test="add-comment"
        full-width
        @click="openTextArea"
      >
        {{ `${$t('Add comment')}...` }}
      </WdsButton>
    </WdsStack>
    <div v-if="errors?.comment?.at(0)" class="tw-text-red-500">{{ errors.comment[0] }}</div>
  </div>
</template>

<script>
import {
  WdsStack,
  WdsAvatarLabel,
  WdsRadio,
  WdsRadioGroup,
  WdsSelect,
  WdsButtonGhost,
  WdsIcon,
  WdsInput,
} from '@worksome/wds-components'
import { nextTick } from 'vue'
import BaseTextareaAutoresize from '@/components/BaseTextareaAutoresize.vue'
import PickerDatetime from '@/components/PickerDatetime.vue'
import InputCurrency from '@/components/InputCurrency.vue'
import { getRateAsIntegerAndFraction } from '@/components/utilities/numberFormatting'
import PickerFileFilebank from '@/components/PickerFileFilebank.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { deepClone } from '@/utils/tools'
import { getAvatarPath } from '@/utils/avatar'

export default {
  components: {
    PickerFileFilebank,
    PickerDatetime,
    WdsAvatarLabel,
    BaseTextareaAutoresize,
    InputCurrency,
    WdsStack,
    WdsRadio,
    WdsRadioGroup,
    WdsSelect,
    WdsButtonGhost,
    WdsIcon,
    WdsInput,
  },
  props: {
    globalId: {
      type: String,
      required: true,
    },
    avatarPath: {
      type: String,
      default: '',
    },
    initials: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    currency: {
      type: String,
      required: true,
    },
    initialComment: {
      type: String,
      default: '',
    },
    errors: {
      type: Object,
      default: () => {},
    },
    profileUrl: {
      type: String,
      default: '',
    },
    candidate: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    const rateObj = getRateAsIntegerAndFraction(this.candidate.rate)

    return {
      rateObj,
      namedRoutes,
      rateConstants: {
        HOURLY: 'HOURLY',
        DAILY: 'DAILY',
        MONTHLY: 'MONTHLY',
        FIXED: 'FIXED',
      },
      comment: this.initialComment,
      editingComment: false,
      isMenuOpen: false,
      selectedAvailability: this.candidate.availableFrom ? 'starting_from' : 'immediately',
      links: !this.candidate.links?.length ? [] : deepClone([...this.candidate.links]),
      files: this.candidate.files ? [...this.candidate.files] : [],
      availableFrom: this.candidate.availableFrom,
    }
  },
  watch: {
    links: {
      deep: true,
      handler(links) {
        this.$emit('update:candidate', { ...this.candidate, links })
      },
    },
    files: {
      deep: true,
      handler(files) {
        this.$emit('update:candidate', { ...this.candidate, files })
      },
    },
    availableFrom(availableFrom) {
      this.$emit('update:candidate', { ...this.candidate, availableFrom })
    },
    selectedAvailability(value) {
      if (value === 'immediately') {
        this.$emit('update:candidate', { ...this.candidate, availableFrom: null })
      }
    },
  },
  methods: {
    laravelNamedRoute,
    getAvatarPath,
    removeFreelancer() {
      this.$emit('removeFreelancer', this.globalId)
    },
    openTextArea() {
      this.editingComment = true
      nextTick(() => {
        this.$refs.freelancerComment.focus()
      })
    },
    saveComment() {
      this.editingComment = false
      this.$emit('update:candidate', { ...this.candidate, comment: this.comment || null })
    },
    cancelComment() {
      this.editingComment = false
      this.comment = this.initialComment
    },
    deleteComment(event) {
      // Stop propagation so that the text area does not open for editing
      event.stopPropagation()
      this.comment = null
      this.$emit('update:candidate', { ...this.candidate, comment: this.comment })
      this.isMenuOpen = false
    },
    openMenu(event) {
      // Stop propagation so that the text area does not open for editing
      event.stopPropagation()
      this.isMenuOpen = true
    },
    closeMenu() {
      this.isMenuOpen = false
    },
    addLink(url) {
      const hasEmptyRows = this.links.some((link) => link.url === '')

      if (!hasEmptyRows) {
        this.links = [...this.links, { key: this.links.length, url }]
      }
    },
    removeLink(linkIndex) {
      this.links.splice(linkIndex, 1)
    },
    handleRateTypeSelected({ value: rateType }) {
      if (!this.rateObj.fraction && !this.rateObj.integer) {
        if (rateType === this.rateConstants.HOURLY && this.candidate.hourRate) {
          this.rateObj = getRateAsIntegerAndFraction(this.candidate.hourRate)
        } else if (rateType === this.rateConstants.DAILY && this.candidate.dailyRate) {
          this.rateObj = getRateAsIntegerAndFraction(this.candidate.dailyRate)
        } else if (rateType === this.rateConstants.MONTHLY && this.candidate.monthlyRate) {
          this.rateObj = getRateAsIntegerAndFraction(this.candidate.monthlyRate)
        }
      }

      this.$emit('update:candidate', {
        ...this.candidate,
        rate: this.convertRateObjToDecimal(this.rateObj),
        rateType,
      })
    },
    updateRate(newRate) {
      const rateAsDecimal = this.convertRateObjToDecimal(newRate)
      this.$emit('update:candidate', { ...this.candidate, rate: rateAsDecimal })
    },
    uploadFiles(files) {
      this.files = [
        ...this.files,
        ...files.map((file) => ({
          name: file.name,
          localId: file.localId,
          globalId: file.globalId,
          storageName: file.storageName,
        })),
      ]
    },
    removeFile(fileGlobalId) {
      this.files = this.files.filter((file) => file.globalId !== fileGlobalId)
    },
    convertRateObjToDecimal(rateObj) {
      return (
        (rateObj.integer || rateObj.fraction) &&
        parseFloat(`${rateObj.integer}.${rateObj.fraction}`)
      )
    },
  },
}
</script>

<style scoped lang="scss">
@import 'resources/sass/frontend/_color-variables';

.close-icon {
  flex-shrink: 0;
}

.pitch-freelancer-card {
  width: 48%;
  background-color: #ffffff;
  border-radius: 4px;
  padding: 24px;
  @apply tw-shadow-sm;
  margin-top: 28px;
}

.rate-title {
  margin-top: 16px;
  margin-bottom: 8px;
  text-transform: capitalize;
}

.freelancer-comment {
  min-height: 128px !important;
  padding-bottom: 55px !important;
}

.existing-comment {
  background-color: #f7f7f7;
  padding: 8px 16px;
}

.comment-wrapper {
  position: relative;
  border-radius: 4px;

  .comment-actions {
    position: absolute;
    bottom: 10px;
    right: 10px;
  }
}

.rate-input-wrapper {
  width: 50%;
  margin-bottom: 16px;
}

.zmdi-close {
  background: $truegray-300;
  border-radius: 100%;
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:hover {
    background: $truegray-400;
  }
}

.add-comment-button {
  justify-content: left !important;
}
.comment-menu {
  position: relative;
  cursor: pointer;
  // Increase clickable area of menu icon because it is a very small target otherwise
  &:after {
    content: '';
    position: absolute;
    left: -5px;
    right: -5px;
    top: -2px;
    bottom: 6px;
  }

  .comment-menu-item {
    position: absolute;
    background-color: white;
    border-radius: 4px;
    @apply tw-shadow-sm;
    right: 0px;
    padding: 8px;
    white-space: nowrap;

    .comment-menu-item-delete {
      background-color: white;
      border-radius: 4px;
      padding: 4px 8px;
      font-weight: 500;
      &:hover {
        background-color: $red-50;
        color: $red-500;
      }
    }
  }
}
</style>
